<script setup lang="ts">
import type { StrapiBlock, StrapiBlockInlineNonText } from '@nimbus/types'
import { computed } from '#imports'

export interface StrapiGeneralTermsSubSection {
    routeHash: string
    content: Array<StrapiBlock | StrapiBlockInlineNonText>
    bullet: string
    newTabLinks?: boolean
}

export interface StrapiGeneralTermsSection {
    routeHash: string
    title: string
    subSections: StrapiGeneralTermsSubSection[]
}

export interface StrapiGeneralTerms {
    header: string
    subHeader: Array<StrapiBlock | StrapiBlockInlineNonText>
    sections?: StrapiGeneralTermsSection[]
}

export interface StrapiGeneralTermsProps {
    content: StrapiGeneralTerms
    useRouteHashes?: boolean
}

const props = defineProps<StrapiGeneralTermsProps>()

function routeHash(routeHash: string): string | null {
    return props.useRouteHashes ? routeHash : null
}

function sanitizeRouteHashId(routeHash: string, bullet: string): string {
    const paragraphNum = bullet.replace(/[.,]/g, '-')
    return `p${paragraphNum}_${routeHash}`
}

const sections = computed(() => {
    return props.content.sections?.map((section: StrapiGeneralTermsSection) => ({
        title: section.title,
        sectionRouteHash: routeHash(`sec_${section.routeHash}`),
        subSections: section.subSections.map((subSection: StrapiGeneralTermsSubSection) => ({
            bullet: subSection.bullet,
            paragraphRouteHash: routeHash(sanitizeRouteHashId(subSection.routeHash, subSection.bullet)),
            paragraph: subSection.content,
            ...(subSection?.newTabLinks && { newTabLinks: subSection.newTabLinks })
        }))
    }))
})
</script>

<template>
    <section>
        <h1>
            <b>{{ content.header }}</b>
        </h1>
        <NimbusStrapiBlockRenderer
            :content="content.subHeader"
        />
        <div
            v-for="({
                title,
                sectionRouteHash,
                subSections
            }) in sections"
            :key="title"
            class="sections"
        >
            <NimbusStrapiLinkToSection :route-hash="sectionRouteHash">
                <h4>
                    <b>{{ title }}</b>
                </h4>
            </NimbusStrapiLinkToSection>
            <div
                v-for="({
                    paragraphRouteHash,
                    bullet,
                    paragraph,
                    newTabLinks
                }) in subSections"
                :key="bullet"
                class="sub-sections"
            >
                <NimbusStrapiLinkToSection :route-hash="paragraphRouteHash">
                    <b>{{ bullet }}</b>
                </NimbusStrapiLinkToSection>
                <div class="sub-sections__content">
                    <NimbusStrapiBlockRenderer
                        :new-tab-links="newTabLinks"
                        :content="paragraph"
                    />
                </div>
            </div>
        </div>
    </section>
</template>

<style scoped lang="scss">
.sections {
    margin-top: 40px;
}

.sub-sections {
    display: flex;
    gap: 10px;

    &__content {
        width: 100%;
    }
}
</style>
